import { useState, useEffect } from "react";
import { toast } from "react-toastify";

// Form validation rules
const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]+$/,
    message: "Name must be 2-50 characters and contain only letters",
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: "Please enter a valid email address",
  },
  phone: {
    required: false,
    pattern: /^[0-9]{10}$/,
    maxLength: 10,
    message: "Phone number must be exactly 10 digits",
  },
  password: {
    required: true,
    minLength: 6,
    maxLength: 20,
    message: "Password must be 6-20 characters long",
  },
};

// Department options removed as not needed

const useAddUserFormData = ({ id, initialData = {}, onSubmit, onCancel }) => {
  const isEdit = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    role: "ops",
    name: "",
    email: "",
    phone: "",
    password: "",
    isActive: true,
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({});

  // Initialize form data for edit mode
  useEffect(() => {
    if (isEdit && initialData) {
      setFormData((prev) => ({
        ...prev,
        name: initialData.name || "",
        email: initialData.email || "",
        phone: initialData.phone || initialData.phoneNumber || "", // Handle both field names
        role: initialData.role || "ops",
        isActive:
          initialData.isActive !== undefined
            ? initialData.isActive
            : initialData.status === "active" ||
              initialData.status !== "inactive",
      }));
    }
  }, [isEdit, initialData]);

  // Validation function
  const validateField = (name, value) => {
    const rule = VALIDATION_RULES[name];
    if (!rule) return "";

    // Required field validation
    if (rule.required && !value.trim()) {
      return `${name.charAt(0).toUpperCase() + name.slice(1)} is required`;
    }

    // Skip other validations if field is empty and not required
    if (!value.trim() && !rule.required) return "";

    // Length validation
    if (rule.minLength && value.length < rule.minLength) {
      return `${
        name.charAt(0).toUpperCase() + name.slice(1)
      } must be at least ${rule.minLength} characters`;
    }

    if (rule.maxLength && value.length > rule.maxLength) {
      return `${name.charAt(0).toUpperCase() + name.slice(1)} must not exceed ${
        rule.maxLength
      } characters`;
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(value)) {
      return rule.message;
    }

    // Special validation for password in edit mode
    if (name === "password" && isEdit && !value.trim()) {
      return ""; // Password is optional in edit mode
    }

    return "";
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach((field) => {
      if (field === "password" && isEdit && !formData.password) {
        return; // Skip password validation in edit mode if empty
      }
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Special handling for phone number
    if (name === "phone") {
      // Remove all non-numeric characters
      processedValue = value.replace(/[^0-9]/g, "");
      // Limit to 10 digits
      if (processedValue.length > 10) {
        processedValue = processedValue.slice(0, 10);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle input blur (for validation)
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate field on blur
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Mark all fields as touched to show errors
      const allTouched = {};
      Object.keys(formData).forEach((key) => {
        allTouched[key] = true;
      });
      setTouched(allTouched);

      // Show validation error toast
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare user data
      const userData = {
        ...(isEdit ? { id } : {}),
        role: formData.role,
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        phoneNumber: formData.phone.trim(), // Include both field names for compatibility
        isActive: formData.isActive,
        status: formData.isActive ? "active" : "inactive", // Include status field for compatibility
        ...(formData.password ? { password: formData.password } : {}),
      };

      // Call onSubmit callback
      if (onSubmit) {
        await onSubmit(userData);
      }

      // Reset form if adding new user
      if (!isEdit) {
        setFormData({
          role: "ops",
          name: "",
          email: "",
          phone: "",
          password: "",
          isActive: true,
        });
        setTouched({});
        setErrors({});
      }
    } catch (error) {
      console.error("Form submission error:", error);

      // Show error toast
      const errorMessage = isEdit
        ? "Failed to update user. Please try again."
        : "Failed to create user. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Get field error (only show if touched)
  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  // Check if field has error
  const hasFieldError = (fieldName) => {
    return Boolean(touched[fieldName] && errors[fieldName]);
  };

  return {
    // Form state
    formData,
    errors,
    isSubmitting,
    isEdit,

    // Handlers
    handleChange,
    handleBlur,
    handleSubmit,
    handleCancel,

    // Validation helpers
    getFieldError,
    hasFieldError,
  };
};

export default useAddUserFormData;
